/**
 * FULL-SCREEN PLAYER — the now-playing view.
 *
 * Not an enlarged transport bar. The transport is a strip of controls whose job is to
 * stay out of the way; this is the opposite — the artwork is the subject, at the
 * largest size the viewport allows, with the controls arranged beneath it in the shape
 * every music player uses. Same state, same actions, different priority.
 *
 * ONE PLAYER, NOT TWO. Every control here calls the same handler the transport's
 * equivalent does, so the two are views of one playerStore: pause in here and the
 * transport shows paused, seek in here and the timeline playhead moves. Nothing about
 * playback is owned locally. The only local state is `native`, which tracks whether
 * the browser granted real fullscreen.
 *
 * THE ARTWORK is the song's canonical artwork, resolved through the same SongArtwork
 * every other surface uses — so an uploaded cover shows here, a generated one shows
 * here, and replacing it in the Cover Art panel changes this too. No second artwork
 * lookup, which is the rule lib/artwork exists to enforce.
 *
 * THE FULLSCREEN API, and why this works without it. The overlay is a portalled
 * `position: fixed; inset: 0` surface, which covers the viewport by itself; requesting
 * real fullscreen on top of that additionally hides the browser's own chrome. So the
 * request is an enhancement and its failure is not an error: `requestFullscreen`
 * rejects on iOS Safari for a non-video element, and is disallowed outright inside a
 * sandboxed iframe. Both cases land in the same catch and the view still works, which
 * is the graceful fallback — the `native` flag only decides which icon the toggle
 * shows, never whether the player renders.
 *
 * ESCAPE closes it, and it closes exactly once: when the browser leaves native
 * fullscreen it fires `fullscreenchange`, so a single Escape both exits fullscreen and
 * dismisses the view. See the listener.
 *
 * SIMULATED. There is no audio here either — see src/stores/playerStore.ts. Everything
 * this view shows and changes is real state; what is missing is sound. The speed
 * selector included: it changes the rate the position advances at, which is what
 * playback is here, and cannot change a pitch. See src/lib/playback.ts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Loader2,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Repeat,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import SeekBar from './SeekBar';
import { LyricSheet, SongArtwork } from '../songs';
import { IconButton, Tooltip } from '../ui';
import { wrapTabFocus } from '../../lib/focusTrap';
import { formatTime, joinWithMiddleDot, parseLength } from '../../lib/formatting';
import { PLAYBACK_SPEEDS } from '../../data/music';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface FullscreenPlayerProps {
  open: boolean;
  song: Song;
  /** Creator/artist line. Absent when the account name is not available. */
  artist?: string;
  playing: boolean;
  /**
   * True when this song is loaded, running or paused.
   *
   * The seek bar and the clock read this so a pause holds its position; the play
   * button's label reads `playing`. Defaults to `playing`.
   */
  active?: boolean;
  elapsed: number;
  /** Seconds buffered from the start of the media, when known. */
  bufferedSec?: number;
  /**
   * The loaded track's runtime in seconds, when the player has resolved one.
   *
   * The seek bar spans this rather than the song's declared length, so a loaded
   * VERSION — which an edit can make shorter or longer than the song — makes the bar
   * complete on the version's runtime. Falls back to `parseLength(song.length)` when
   * absent or non-positive, and is only consulted while `active`. The total-time label
   * beside it is already version-aware via `totalLabel`. See usePlayer's `durationSec`.
   */
  durationSec?: number;
  loading?: boolean;
  volume: number;
  speed: number;
  repeat: boolean;
  muted: boolean;
  onClose: () => void;
  onTogglePlay: () => void;
  onSeek: (seconds: number) => void;
  onSetVolume: (volume: number) => void;
  onSetSpeed: (speed: number) => void;
  onToggleRepeat: () => void;
  onToggleMute: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export default function FullscreenPlayer({
  open,
  song,
  artist,
  playing,
  active = playing,
  elapsed,
  bufferedSec = 0,
  durationSec,
  loading = false,
  volume,
  speed,
  repeat,
  muted,
  onClose,
  onTogglePlay,
  onSeek,
  onSetVolume,
  onSetSpeed,
  onToggleRepeat,
  onToggleMute,
  onPrev,
  onNext,
  totalLabel,
}: FullscreenPlayerProps & { totalLabel: string }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  /** True while the browser has actually granted native fullscreen. */
  const [native, setNative] = useState(false);
  const [showLyrics, setShowLyrics] = useState(false);

  // The seek bar's span: the version-aware runtime while this song is active, the song's
  // declared length otherwise. Matches the transport's rule and the `totalLabel` above,
  // so the bar completes exactly where the total time reads. `> 0` guards a 0 duration.
  const totalSec =
    active && durationSec && durationSec > 0 ? durationSec : parseLength(song.length);
  // `active`, so pausing here holds the position rather than snapping the bar and the
  // clock to zero. The same rule the transport's `pos` follows.
  const pos = active ? elapsed : 0;
  const lyricPosition = active && totalSec > 0 ? elapsed / totalSec : null;
  const lyricElapsedSec = active ? elapsed : null;

  /**
   * Asks for native fullscreen. Silently does nothing when it is unavailable.
   *
   * Both the guard and the catch are needed: older WebKit has no
   * `requestFullscreen` on an element at all, and browsers that do have it can
   * still reject the promise (iOS Safari for non-video, any sandboxed iframe
   * without allow-fullscreen). Neither is a failure worth telling the user about —
   * the overlay already fills the viewport.
   */
  const enterNative = useCallback(() => {
    const el = rootRef.current;
    if (!el?.requestFullscreen) return;
    el.requestFullscreen().catch(() => {
      /* Not available. The overlay stands on its own. */
    });
  }, []);

  const exitNative = useCallback(() => {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {
        /* Already out, or refused. Nothing to undo. */
      });
    }
  }, []);

  // Try for native fullscreen on open. A rejection is expected on some platforms
  // and is handled inside enterNative.
  useEffect(() => {
    if (!open) return;
    enterNative();
    return exitNative;
  }, [open, enterNative, exitNative]);

  /*
    Track what the browser actually did, rather than what we asked for.

    This is also what makes Escape work once instead of twice. Escape while in
    native fullscreen is handled by the browser, not by us — it exits fullscreen and
    our keydown listener never sees it. So leaving fullscreen is treated as a
    request to close the view, which is what the user meant by pressing it.
  */
  useEffect(() => {
    if (!open) return;
    const onChange = () => {
      const isNative = document.fullscreenElement === rootRef.current;
      setNative(isNative);
      if (!isNative) onClose();
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [open, onClose]);

  // Escape for the non-native case, where the browser has nothing to intercept.
  // Capture phase and stopPropagation, so the Studio's own Escape shortcut — which
  // closes a tool panel or leaves the Studio entirely — does not also fire.
  //
  // TAB IS CONTAINED HERE TOO, which this surface was the last modal one without. It
  // declares role="dialog" aria-modal="true", covers the viewport, moves focus in and locks
  // background scroll — and then let Tab walk straight out into the page underneath, so a
  // keyboard user pressing Tab past the volume slider was operating the Studio through an
  // opaque overlay with no way to tell where they were. Modal.tsx and VaultPackDrawer.tsx
  // both carry this same wrap; the note in Modal applies unchanged: there is no platform
  // primitive short of `inert` on everything else.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (document.fullscreenElement) return;
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = rootRef.current;
      if (!root) return;
      wrapTabFocus(root, e);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose]);

  // Focus in on open, back out on close, and the page locked behind it — the same
  // three things Modal does, for the same reason: this is a modal surface.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    rootRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      const target = previouslyFocused.current;
      if (target && document.contains(target)) target.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setShowLyrics(false);
  }, [open, song.id]);

  if (!open) return null;

  return createPortal(
    <div
      ref={rootRef}
      className="lyx-fs"
      role="dialog"
      aria-modal="true"
      aria-label={`Now playing — ${song.title}`}
      tabIndex={-1}
      style={{
        // Portalled to body, so neither colour nor font is inherited — the same
        // note as Modal's and Toast's headers.
        color: c.text1,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* ---- top row: close, and the native-fullscreen toggle ---- */}
      <div className="lyx-fs-top">
        <Tooltip label="Close full-screen player" placement="bottom">
          <IconButton
            label="Close full-screen player"
            nativeTitle={false}
            onClick={onClose}
            style={{ width: 36, height: 36, borderRadius: 10, background: c.surface2 }}
          >
            <X size={16} color={c.text1} />
          </IconButton>
        </Tooltip>

        <span className="lyx-fs-eyebrow">Now playing</span>

        {/*
          Enter/exit *native* fullscreen without leaving the view. Rendered only
          where the API exists, because a button that cannot do its one job is worse
          than no button — and the view is already full-viewport either way, so
          there is nothing lost by its absence. This is not the control that closes
          the player; that is the X.
        */}
        {typeof document !== 'undefined' && document.fullscreenEnabled ? (
          <Tooltip
            label={native ? 'Exit browser full screen' : 'Fill the screen'}
            placement="bottom"
          >
            <IconButton
              label={native ? 'Exit browser full screen' : 'Fill the screen'}
              nativeTitle={false}
              pressed={native}
              onClick={() => (native ? exitNative() : enterNative())}
              style={{ width: 36, height: 36, borderRadius: 10, background: c.surface2 }}
            >
              {native ? (
                <Minimize2 size={15} color={c.text2} />
              ) : (
                <Maximize2 size={15} color={c.text2} />
              )}
            </IconButton>
          </Tooltip>
        ) : (
          // Keeps the title centred in the three-column row when the toggle is
          // absent, rather than letting it drift right.
          <span aria-hidden="true" style={{ width: 36 }} />
        )}
      </div>

      {/* ---- the artwork ----
          Sized by the CSS (a square that takes the smaller of the available width
          and height) rather than by a pixel value here, so it scales with the
          viewport instead of being clamped by an inline style a media query cannot
          override. See .lyx-fs-art. */}
      <button
        type="button"
        className="lyx-fs-art"
        onClick={() => setShowLyrics((value) => !value)}
        aria-label={
          showLyrics ? `Show cover art for ${song.title}` : `Show lyrics for ${song.title}`
        }
        style={{
          background: c.surface2,
          border: `1px solid ${c.border}`,
          padding: 0,
          cursor: 'pointer',
          color: c.text1,
          aspectRatio: '1 / 1',
        }}
      >
        {showLyrics ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              padding: 18,
              boxSizing: 'border-box',
              overflowY: 'auto',
              textAlign: 'left',
              color: c.text2,
            }}
          >
            {song.lyrics?.trim() ? (
              <LyricSheet
                lyrics={song.lyrics}
                at={lyricPosition}
                elapsedSec={lyricElapsedSec}
                lyricTimings={song.lyricTimings}
                lyricSectionTimings={song.lyricSectionTimings}
              />
            ) : (
              <p style={{ fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                Lyrics are not available for this song yet.
              </p>
            )}
          </div>
        ) : (
          <SongArtwork
            song={song}
            depth="hero"
            radius={18}
            style={{ width: '100%', height: '100%' }}
          />
        )}
      </button>

      {/* ---- title block ---- */}
      <div className="lyx-fs-meta">
        <h2 className="lyx-fs-title">{song.title}</h2>
        <p className="lyx-fs-sub">
          {/* Creator first when known, then the facts the song actually carries.
              Joined with a middle dot rather than rendered as separate lines, so a
              phone does not spend four rows on metadata. */}
          {joinWithMiddleDot([artist, song.genre, song.mood])}
        </p>
      </div>

      {/* ---- position ---- */}
      <div className="lyx-fs-position">
        <SeekBar
          value={pos}
          bufferedSec={bufferedSec}
          duration={totalSec}
          onSeek={onSeek}
          playing={playing}
          // One tick of smoothing rather than a fixed second, so the fill stays level
          // with the clock beneath it when the selector below is set to 1.5× or 2×.
          speed={speed}
          label={`Seek within ${song.title}`}
          // Thicker than the transport's 4px: this is the primary control on the
          // screen rather than a strip inside a toolbar.
          height={6}
        />
        <div className="lyx-fs-times">
          <span>{formatTime(pos)}</span>
          <span>{totalLabel}</span>
        </div>
      </div>

      {/* ---- primary transport ---- */}
      <div className="lyx-fs-controls">
        <Tooltip label="Previous track">
          <IconButton
            label="Previous track"
            nativeTitle={false}
            onClick={onPrev}
            disabled={!onPrev}
            style={{ width: 44, height: 44, borderRadius: 12 }}
          >
            <SkipBack size={20} color={c.text2} />
          </IconButton>
        </Tooltip>

        <button
          type="button"
          onClick={onTogglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
          className="lyx-fs-play"
        >
          {loading ? (
            <Loader2 size={22} color="#1A1300" className="lyx-spin" />
          ) : playing ? (
            <Pause size={22} color="#1A1300" />
          ) : (
            <Play size={22} color="#1A1300" style={{ marginLeft: 2 }} />
          )}
        </button>

        <Tooltip label="Next track">
          <IconButton
            label="Next track"
            nativeTitle={false}
            onClick={onNext}
            disabled={!onNext}
            style={{ width: 44, height: 44, borderRadius: 12 }}
          >
            <SkipForward size={20} color={c.text2} />
          </IconButton>
        </Tooltip>
      </div>

      {/* ---- secondary: repeat, volume, speed ---- */}
      <div className="lyx-fs-output">
        <Tooltip label="Repeat this song">
          <IconButton
            label="Repeat"
            nativeTitle={false}
            pressed={repeat}
            onClick={onToggleRepeat}
            style={{ width: 34, height: 34, borderRadius: 9 }}
          >
            <Repeat size={16} color={repeat ? c.accent : c.text2} />
          </IconButton>
        </Tooltip>

        <span className="lyx-fs-volume">
          <Tooltip label="Mute / unmute">
            <IconButton
              label={muted ? 'Unmute' : 'Mute'}
              nativeTitle={false}
              pressed={muted}
              onClick={onToggleMute}
              style={{ width: 34, height: 34, borderRadius: 9 }}
            >
              {muted ? (
                <VolumeX size={16} color={c.accent} />
              ) : (
                <Volume2 size={16} color={c.text2} />
              )}
            </IconButton>
          </Tooltip>
          <input
            type="range"
            min={0}
            max={100}
            value={volume}
            onChange={(e) => onSetVolume(Number(e.target.value))}
            aria-label="Volume"
            className="lyx-fs-volume-slider"
            /*
              The width lives in CSS, not here. `width: 108` was an inline style prop,
              and an inline prop outranks a media query — so on a 360px phone the slider
              held its desktop width and pushed the speed selector onto a second row (or
              past the edge, before .lyx-fs-output learned to wrap). Same defect the
              transport's slider had; see styles/responsive.test.ts, which pins both.

              accentColor stays inline: it is state, not a dimension, and it tracks
              `muted` from the store. text3 is legitimate here — it is the track fill of
              a control whose muted state is already stated by the VolumeX icon beside
              it in accent, so nothing depends on reading this.
            */
            style={{ accentColor: muted ? c.text3 : c.accentFill }}
          />
        </span>

        <Tooltip label="Playback speed">
          <select
            value={speed}
            onChange={(e) => onSetSpeed(Number(e.target.value))}
            aria-label="Playback speed"
            style={{
              fontSize: 12,
              background: c.surface2,
              border: `1px solid ${c.border}`,
              borderRadius: 7,
              color: c.text2,
              padding: '6px 8px',
              fontFamily: 'IBM Plex Mono',
            }}
          >
            {PLAYBACK_SPEEDS.map((sp) => (
              <option key={sp} value={sp}>
                {sp}×
              </option>
            ))}
          </select>
        </Tooltip>
      </div>
    </div>,
    document.body
  );
}
